<template>
	<div v-if="type == 'vehicles'" id="vehicle-search-table">
    <v-row>
      <v-col cols="3">
          <div class="vehicles-search-bar">
              <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Refine"
                  single-line
                  hide-details
              ></v-text-field>
          </div>
      </v-col>

      <TableRefresh 
				class="pt-1"
        :state="loading"
        @click.native="refreshTable"
      />
			<v-spacer/>
        <v-col cols="1">
          <v-icon v-if="!excelLoading" :disabled="loadingPresence" :loading="loadingPresence" @click="exportExcel">
            mdi-file
          </v-icon>
          <v-icon v-else class="updating">
            mdi-refresh
          </v-icon>
        </v-col>
    </v-row>


		<v-data-table
			:headers="headers"
			:items="tableData"
			:loading="loading || loadingPresence"
			:search="search"
			:sort-desc="true"
      class="elevation-1 admin-table click-row"
      :items-per-page="100"
      hide-default-footer
		>
			<template v-slot:progress>
				<v-progress-linear color="blue" height="2" indeterminate></v-progress-linear>
			</template>

      <template v-slot:item.install_tag="{item}">
        {{ returnInstallTag(item) }}
      </template>

			<template v-slot:item.status="{ item }">
				<v-tooltip bottom>
					<template v-slot:activator="{ on, attrs }">
						<v-avatar
							:color="mapStatusColor(item)"
							size="20"
							v-bind="attrs"
							v-on="on"
						>
						</v-avatar>
					</template>
					<span>{{ getCurrentStatus(item) }}</span>
				</v-tooltip>
			</template>

			<template v-slot:item.last_seen="{ item }">
				{{ getLastSeen(item) }}
			</template>

			<template v-slot:item.action="{item}">
				<div class="reports-action-launch" @click="launchRowAction">
					<v-icon>mdi-dots-vertical</v-icon>
					<ActionContainer :ref="item.key" :heading="'Action'" class="report-action-options">
						<ActionItem 
							:disabled="!$can('updateVehicle')"
							label="Edit Vehicle"
							icon="mdi-pencil"
							@click.native="toggleConfigComponent(item, 'vehicle')"/>

						<ActionItem 
							:disabled="!$can('viewDeviceInfo')"
							icon="mdi-laptop"
							label="Device Info"
							@click.native="toggleConfigComponent(item, 'device')"/>

						<ActionItem 
							:disabled="!$can('editAlertConfig')"
							icon="mdi-bell"
							label="Alert Config"
							@click.native="toggleConfigComponent(item, 'alert')"/>

						<ActionItem 
							:disabled="!$can('viewDeviceInfo')"
							label="Device Config"
							icon="mdi-settings"
							@click.native="toggleDeviceConfig(item)"/>

						<ActionItem 
							label="Retrieve Logs"
							icon="mdi-file"
							@click.native="toggleLogComp(item)"/>

						<ActionItem 
							label="Reboot Device"
							icon="mdi-power"
							@click.native="toggleReboot(item)"/>
						
						<ActionItem 
							:disabled="!$can('deleteVehicle')"
							label="Delete"
							icon="mdi-delete"
							@click.native="toggleDeletePrompt(item)"/>

						<ActionItem 
							label="Format Disk"
							icon="mdi-harddisk"
							@click.native="toggleFormat(item)"/>

						<ShowOnMap v-if="item.devices.length > 0" :org="item.org_id" :vehicle="item" :device="item.devices && item.devices.length ? item.devices[0].id : false" />

						<ActionItem
							:disabled="!$can('createFirmwareUpdateJob') && !$can('createDeviceConfigJob') && !$can('createDeviceInstallJob')"
							label="Create Job"
							icon="mdi-clipboard-list"
							@click.native="toggleJobCreation(item)"/>
					</ActionContainer>
				</div>
			</template>
	
			<template v-slot:item.notes="{ item }">
        <v-layout justify-center>
          <v-icon @click.native="toggleConfigComponent(item, 'vehicle')">
            mdi-file
          </v-icon>
        </v-layout>
      </template>
		</v-data-table>

    <Pagination
      :pagination-info="paginationInfo"
      @updatedPage="fetchNewPage"
    />

		<DownloadLog 
			:display="showLogs"
			:device="logItem" 
			:org="orgId"
			@closeLogComp="closeLogComp"
		/>

		<ConfigPanel 
			v-if="displayConfig"
			:items="selectedItems"
			:groups="vehicleGroup"
			:vehicle-types="vehiType"
			:multi-device="false"
			:org="orgId"
			:nodes="nodes"
			:selected-contacts="selectedContacts"
			@toggleModal="toggleModal"
			@refreshData="refreshData"
		/>

		<InfoContainer
      :display="displayVehicleInfo"
      :width="600"
      :panel-heading="panelHeading"
      @closeModal="displayVehicleInfo = false"
    >
      <pvcam-wc-vehicle-config 
        :items="JSON.stringify(selectedItems)"
        :groups="JSON.stringify([vehicleGroup])"
        :org="JSON.stringify(orgId)"
        :vehicle-types="JSON.stringify(vehiType)"
        :nodes="JSON.stringify(null)"
        :multi-device="JSON.stringify(multiDevices)"
				:admin="true"
				:translate="JSON.stringify(false)"
        @showSelectContacts="handleSelection"
        @refreshData="refreshData"
      />
    </InfoContainer>

		<InfoContainer
      :display="displayDeviceInfo"
      :width="1000"
      :panel-heading="panelHeading"
      @closeModal="displayDeviceInfo = false"
    >
      <pvcam-wc-device-info
        :org="JSON.stringify(orgId)"
        :items="JSON.stringify(selectedItems)"
      />
    </InfoContainer>

		<InfoContainer
      :display="displayAlertConfig"
      :width="600"
      :panel-heading="panelHeading"
      @closeModal="displayAlertConfig = false"
    >
      <pvcam-wc-device-alert-config
        :items="JSON.stringify(selectedItems)"
        :groups="JSON.stringify(vehicleGroup)"
        :org="JSON.stringify(orgId)"
        :vehicle-types="JSON.stringify(vehiType)"
        :nodes="JSON.stringify(nodes.root_node)"
        :multi-config="JSON.stringify(displayMultiConfig)"
        :selected-contacts="JSON.stringify(selectedContacts)"
        :user-info="JSON.stringify({})"
				:admin-user="true"
        @showSelectContacts="handleSelection"
        @refreshData="refreshData"
      />
    </InfoContainer>

		<DeviceConfig 
			v-if="displayDeviceConfig"
			:item="selectedDevice"
			:org="orgIdFromDevice"
			@closeDeviceConfig="displayDeviceConfig = !displayDeviceConfig"/>

		<Prompt
			:display="deleteLogConfirm"
			:close-target="'closeDelete'"
			:submit-target="'submitDeleteLog'"
			:title="'Delete Vehicle'"
			:message="deleteMessage"
			:state="deleteLogState" 
			:res="deleteLogRes"
			@closeDelete="toggleDeletePrompt"
			@submitDeleteLog="deleteItem"
		/>

		<Contacts 
			v-if="orgId"
      v-model="shared" 
			:org-id="orgId"
      :show="showContacts"  
      @close="showContacts = false"
      @input="handleSelection"
		/>

		<DeviceFormat 
			v-if="displayFormat && selectedItems.length > 0"
			:device="selectedItems[0]"
			:org="orgId"
			@close="toggleFormat(null)"
		/>
		<DeviceReboot 
			v-if="displayReboot  && selectedItems.length > 0"
			:org="orgId"
			:device="selectedItems[0]"
			@close="toggleReboot(null)"
		/>

		<add-job 
			:key="refreshAddJob"
			:open="jobVisibility" 
			:mode="mode" 
			:job-item="jobItem"
			@exit="toggleJobCreation(null)"
		/>
	</div>
</template>

<script>
	import { mapState, mapMutations } from "vuex";
	import ShowOnMap from '@/components/general/menu/ShowOnMap.vue'
	import {
		GetOrgVehicles,
		DeleteOrgVehicle,
		UnassignDeviceFromVehicle,
		GetOrgVehiclePresence
	} from "@/services/API.js";
  import { search } from '@/mixins/quickSearch.js'

	import * as xlsx from 'xlsx'

	import moment from "moment";

	// import DeleteVehicle from '../components/DeleteVehicle.vue';
	import ConfigPanel from '@/views/OrgVehicles/Sections/ConfigPanel.vue';
	import Contacts from '@/components/contacts/ContactsModal.vue';
	import DeviceConfig from '@/components/deviceConfig/config.vue';
	import DownloadLog from '@/views/OrgVehicles/components/DownloadLog.vue';
	import ActionContainer from '@/components/general/ActionContainer.vue';
	import ActionItem from '@/components/general/ActionItem.vue';
	import { getColor, findDefaultDevice } from '@/utils'
	import TableRefresh from '@/components/general/TableRefresh.vue';
	import Prompt from '@/components/general/Prompt.vue';
	import DeviceFormat from '@/components/general/DeviceFormat.vue';
	import DeviceReboot from '@/components/general/DeviceReboot.vue';
	// import DevicePresenceInfo from '@/components/general/DevicePresenceInfo.vue';
	import AddJob from "@/views/Jobs/Sections/AddJob.vue";
	import InfoContainer from '@/components/general/InfoContainer.vue';
  import Pagination from './Pagination.vue';

	import { Logger } from "aws-amplify";
	const logger = new Logger("org vehicles");

	export default {
    name: 'Vehicles',
		components: {
			ConfigPanel,
			Contacts,
			DeviceConfig,
			DownloadLog,
			ActionContainer,
			ActionItem,
			TableRefresh,
			Prompt,
			DeviceFormat,
			DeviceReboot,
			// DevicePresenceInfo,
			ShowOnMap,
			AddJob,
			InfoContainer,
      Pagination
		},
    mixins: [ search ],
		props: {
			update: {
				type: Number,
				default: 0
			},
			type: {
				required: true,
				type: String
			}
		},
		data: () => ({
			showLogs: false,
			search: '',
			headers: [
				{
          text: 'Status',
          value: 'status',
          align: 'left'
        },
				{
          text: 'Name',
          value: 'vehicle_name',
          align: 'left'
        },
				{
					text: 'Organisation',
          value: 'org',
          align: 'left'
				},
				{
					text: 'Last Seen',
					value: 'last_seen',
					align: 'left'
				},
				{
					text: 'Install Tag',
					value: 'install_tag',
					align: 'left'
				},
        {
          text: 'Type',
          value: 'vehicle_type',
          align: 'left'
        },
        {
          text: 'Group',
          value: 'node_name',
          align: 'left'
        },
        {
          text: 'Make',
          value: 'vehicle_make',
          align: 'left'
        },
        {
          text: 'Model',
          value: 'vehicle_model',
          align: 'left'
        },
        {
          text: 'Fuel (L)',
          value: 'vehicle_fuel',
          align: 'left'
        },
        {
          text: 'Displacement',
          value: 'vehicle_displacement',
          align: 'left'
        },
        {
          text: 'Year',
          value: 'vehicle_year',
          align: 'left'
        },
				{
          text: 'VIN',
          value: 'vehicle_vin',
          align: 'left'
        },
        {
          text: 'Notes',
          value: 'notes',
          align: 'left'
        },
				{ 
					text: "Actions", 
					value: "action", 
					align: 'left'
				}
      ],
			vehicles: [],
			loading: false,
			deletingItem: false,
			loader: false,
			loadingPresence: false,
			noresult: false,
			dialog: false,
			editedIndex: -1,
			editedItem: {
				vehicle_name: "",
				vehicle_type: ""
			},
			defaultItem: {
				vehicle_name: "",
				vehicle_type: ""
			},
			displayConfig: false,
			selectedItems: [],
			nodes: [],
			selectedContacts: {},
			showContacts: false,
			shared: [],
			selectedDevice: null,
			displayDeviceConfig: false,
			date: moment().format("YYYY-MM-DD HH:mm:ss"),
			logItem: {},
			itemForDeletion: {},
			showDelete: false,
			deleteComplete: false,
			deleteLogConfirm: false,
      deleteLogState: false,
      deleteLogRes: null,
      deleteTarget: null,

			displayFormat: false,
			formatItem: null,
			
			displayReboot: false,
			rebootDevice: null,

			devices: [],

      // orgs: [],
      selectedOrg: '',

			mode: "new",
			jobVisibility: false,
      jobItem: null,
      refreshAddJob: 0,

			displayVehicleInfo: false,
			displayDeviceInfo: false,
			displayAlertConfig: false,
			multiDevices: false,
			displayMultiConfig: false,
      paginationInfo: {
        pageNum: 1,
        pageCount: 1,
        pageSize: 100
      },
			orgIds: [],
			presenceData: [],
			tableData: [],
			excelLoading: false
		}),
		computed: {
			...mapState([
        'quickSearchResults',
        'quickSearchPayload'
      ]),
      orgId() {
        return this.selectedItems.length > 0 ? this.selectedItems[0].org_id : '';
      },
			orgIdFromDevice() {
				return this.selectedDevice ? this.selectedDevice.org_id : ''
			},
      vehiType() {
        return this.selectedItems.length > 0 ? this.selectedItems[0].vehicle_type : '';
      },
			deleteMessage() {
				if (this.deleteTarget) {
					return this.deleteTarget.devices.length > 0 ? 
						'This vehicle has an existing device assigned. If you proceed then this device will be unassigned.'
						:
						`Are you sure you wish to delete vehicle : ${this.deleteTarget.vehicle_name}?`;
				}
				else {
					return '';
				}
			},
			panelHeading() {
				return this.selectedItems.length > 0 
					? this.selectedItems.length > 1 ? 'Multiple Vehicles Selected' : this.selectedItems[0].vehicle_name
					:
					'';
			},
      hasData() {
      const data = this.quickSearchResults;

      return Object.keys(data).length > 0;
    },
		vehicleGroup() {
			return this.selectedItems.length > 0 ? this.selectedItems[0].node_name : [];
		},
    currentPage() {
      return this.hasData ? this.quickSearchPayload.pageNum : 1;
    },
    currentRoute() {
      return this.$route.path;
    },
    hasPayload() {
      const data = this.quickSearchPayload;

      return Object.keys(data).length > 0;
    }
		},
		watch: {
			dialog(val) {
				val || this.close();
			},
			update(val) {
				val && this.reload();
			},
      quickSearchResults: {
        handler() {
					logger.info('quick search res')
					this.compileTableData();
          this.buildPaginationInfo();
        },
        deep: true
      },
		},
		mounted() {
			this.compileTableData();
			window.EventBus.$on("wcToggleContacts", this.handleToggleContacts);
			window.EventBus.$on("showSelectContacts", this.handleContactSelection);
			window.EventBus.$on("refreshData", this.refreshData);
			this.buildPaginationInfo();

			window.$translate = (section, value) => {
				logger.info('translate', section, value)
			}
		},
		methods: {
			async exportExcel() {
				this.excelLoading = true
				const exportSheetName = `Vehicles-Global-Search-${moment().format('YYYY-MM-DDTHH:mm:ss')}`

				const headers = [
					'Status',
					'Name',
					'Organisation',
					'Last Seen',
					'Install Tag',
					'Type',
					'Group',
					'Make',
					'Model',
					'Fuel (L)',
					'Displacement',
					'Year',
					'VIN',
					'Notes'
				]

				const payloadCopy = JSON.parse(JSON.stringify(this.quickSearchPayload))
      	const fullSearch = await this.fullSearch(payloadCopy, this.paginationInfo)

				logger.debug("full search vehicles", fullSearch)

				// we need to rebuild the org id list from the full results
				this.orgIds = []

				fullSearch.forEach(vehicle => {
					let id = vehicle.org_id ? vehicle.org_id : null
					if (id && !this.orgIds.includes(id)) {
						this.orgIds.push(id)
					}  
				})

      	await this.getPresenceData()

				const vehicles = fullSearch.map(item => (
					{ 
						...item, 
						install_tag: this.returnInstallTag(item),
						last_seen: this.getLastSeen(item),
						status: this.getCurrentStatus(item)
					}
				))
		
				const data = [
					headers,
					[],
					...vehicles.map(item => [
						item.status,
						item.vehicle_name,
						item.org,
						item.last_seen,
						item.install_tag,
						item.vehicle_type,
						item.node_name,
						item.vehicle_make,
						item.vehicle_model,
						item.vehicle_fuel,
						item.vehicle_displacement,
						item.vehicle_year,
						item.vehicle_vin,
						item.notes
					])
				]

				// Create a new workbook
				const wb = xlsx.utils.book_new()

				const ws = xlsx.utils.aoa_to_sheet(data)

					const colWidths = data[0].map((_, colIndex) => {
						const maxLength = data.reduce((max, row) => {
							const cellValue = row && row[colIndex] ? row[colIndex].toString() : ''
							return Math.max(max, cellValue.length)
						}, 0)
						return { width: maxLength + 2 }; // Add a little extra padding
					})

					// Set column widths in the worksheet
					ws['!cols'] = colWidths

					// Append the worksheet to the workbook
					xlsx.utils.book_append_sheet(wb, ws, 'Vehicles')

					// Write the workbook to a file (e.g., 'example.xlsx')
					xlsx.writeFile(wb, exportSheetName + ".xlsx")
					this.excelLoading = false
			},
			compileTableData() {
				const data = this.quickSearchResults;
				let results = []

				if(this.hasData) {
					if (data.res.Result.length > 0) {
						results = JSON.parse(JSON.stringify(data.res.Result))
					} else {
						results = [];
					}
					
					this.tableData = results.map((vehicle) => {
						vehicle.group = vehicle.node_name;
						vehicle.devices = vehicle.devices.length > 0 ? vehicle.devices.filter(device => device.is_default_device == "true") : [];

						return vehicle;
					});

					if (this.tableData.length > 0) {
						this.compileOrgIds();
					}
				}
				logger.info('table data',this.tableData)
			},
			mapStatusColor(vehicle) {
				const statusObj = this.presenceData.find(item => item.vehicle_id == vehicle.id)

				if(statusObj) {
					return this.statusColor(statusObj.status)
				}
			},
			getCurrentStatus(vehicle) {
				const statusObj = this.presenceData.find(item => item.vehicle_id == vehicle.id)

				if(statusObj) {
					return statusObj.status;
				}
			},
			getLastSeen(vehicle) {
				const statusObj = this.presenceData.find(item => item.vehicle_id == vehicle.id)

				if(statusObj) {
					return moment(statusObj.last_seen_local_time).format('YYYY MMM DD HH:mm:ss');
				} else {
					return 'Unknown';
				}
			},
      async refreshTable() {
        if(!this.hasPayload) return;
        this.loading = true;

        const search = await this.quickSearch(this.quickSearchPayload, this.currentRoute);

        if (search) {
          this.loading = false;
        }
        else {
          this.loading = false;
        }
      },
      async fetchNewPage(page) {
        if(!this.hasData) return
        if(page == this.currentPage) return

        this.loading = true;

        this.$store.commit('updateQuickSearchPage', page);

        const search = await this.quickSearch(this.quickSearchPayload, this.currentRoute);

        if (search) {
          this.$store.commit('setQuickSearchPayload', this.quickSearchPayload);
          this.loading = false;
        }
        else {
          this.loading = false;
        }
        logger.info('handle page update ', page)
      },
			compileOrgIds() {
				logger.info('compileOrgIds called')
				this.orgIds = [];
			
				this.tableData.forEach(vehi => {
					let id = vehi.org_id;

					if (!this.orgIds.includes(id)) {
						this.orgIds.push(id);
					}  
				});

				this.getPresenceData();
			},
			 async getPresenceData() {
				this.loadingPresence = true;
				let collection = [];
				for (let i = 0; i < this.orgIds.length; i++) {
					collection.push(GetOrgVehiclePresence(this.orgIds[i], true).catch(err => logger.debug('could not fetch org', err)))
				}
				
				const results = await Promise.all(collection);
				const combinedResults = [].concat.apply([], results);

				this.presenceData = combinedResults;
				this.loadingPresence = false;
				return true; // return so the await function works in the excel download
			},
      returnInstallTag(item) {
         if(item.devices.length > 0) {
          return item.devices[0].install_tag;
         }
      },
      buildPaginationInfo() {
				logger.info('buildPaginationInfo')
        if(!this.quickSearchResults.hasOwnProperty('res')) return;

        const data = this.quickSearchResults.res;

        if(data.hasOwnProperty('page_number')) {
          this.paginationInfo.pageNum = data.page_number;
        }
        if(data.hasOwnProperty('total_pages')) {
          this.paginationInfo.pageCount = data.total_pages;
        }
        logger.info('has pagination info ', this.paginationInfo)
      },
			toggleJobCreation(item) {
				const defaultDevice = findDefaultDevice(item);

				this.refreshAddJob++;
				this.jobVisibility = !this.jobVisibility;

				if(!defaultDevice) return;
		
        this.jobItem = item;
			},

			toggleConfigComponent(item, display) {
				logger.info('toggleConfigComponent ',item, display)

				const selectedAmount = this.selectedItems.length;
				const displayBoolMap = {
					vehicle: this.displayVehicleInfo,
					device: this.displayDeviceInfo,
					alert: this.displayAlertConfig
				}

				if (item) {
					if(!this.selectedItems.includes(item)) {
						this.selectedItems = [item];
					}
				} else {
					// Clear selection for single select
					if (selectedAmount === 1 && displayBoolMap[display]) {
						this.selectedItems = [];
					}
				}

				switch (display) {
					case 'vehicle': {
						this.displayVehicleInfo = !this.displayVehicleInfo;

						break;
					}
					case 'device': {
						this.displayDeviceInfo = !this.displayDeviceInfo;

						break;
					}
					case 'alert': {
						this.displayAlertConfig = !this.displayAlertConfig;

						break;
					}
				}
			},
			...mapMutations("presence", [
				"setOrgToWatch"
			]),
			mapStatusToVehicle() {
				this.vehicles.forEach(vehicle => {
					let device = vehicle.devices.find(device => device.is_defaultdevice == true);

					vehicle.status = this.vehicleStatus[vehicle.id] ? this.vehicleStatus[vehicle.id] : 'UNKNOWN';
					vehicle.last_seen = device && this.devicesMap[device.device_id] ? moment(this.devicesMap[device.device_id]['data']['last_seen_local_time']).format('YYYY MMM DD HH:mm:ss') : 'Unknown';
				});
			},
			devicePresenceInfo(data) {
				let device = data.devices.find(device => device.is_defaultdevice == true);

				return device && this.devicesMap[device.device_id] ? this.devicesMap[device.device_id] : null;
			},	
			...mapMutations("websockets", ["manualRefresh"]),
			statusColor(status) {
				return getColor(status)
			},
			toggleFormat(item) {
				this.displayFormat = !this.displayFormat;
				if (item) {
					this.selectedItems = [item];
				}
			},
			toggleReboot(item) {
				this.displayReboot = !this.displayReboot;
				if (item) {
					this.selectedItems = [item];
				}
			},
			toggleDeletePrompt(item) {
				this.deleteLogState = false;
				this.deleteTarget = item;
				this.deleteLogRes = null
				this.deleteLogConfirm = !this.deleteLogConfirm;
			},
			closeLogComp() {
				this.showLogs = false;
			},
			toggleLogComp(item) {
				this.showLogs = !this.showLogs;
				if (item) {
					this.logItem = item;
					this.selectedItems = [item];
				}
			},
			toggleDeleteComp(item) {
				this.showDelete = !this.showDelete;
				this.itemForDeletion = item;
			},
			closeDeleteVehicle() {
				this.showDelete = false;
			},
			launchRowAction(e) {
				let allOptions = document.querySelectorAll(".report-action-options");

				for (let option of allOptions) {
					option.classList.remove('active-options');
					if (option === e.target.nextSibling) {
						option.classList.toggle('active-options');
					} 
				}
			},
			handleContactSelection(val) {
				this.showContacts = val;
			},
			handleToggleContacts(context) {
				this.$store.commit('setContactContext', context);
			},
			handleSelection(selection, target) {
				this.selectedContacts = {
					users: selection,
					context: target
				};
			},
			
			toggleModal(item) {
				logger.info('toggleModal ', item)
				let selectedAmount = this.selectedItems.length;

				if (item) {
					if(!this.selectedItems.includes(item)) {
						this.selectedItems.push(item);
					}
				} else {
					// Clear selection for single select
					// logger.info('testing', selectedAmount, this.displayConfig);
					if (selectedAmount === 1 && this.displayConfig) {
						// logger.info('criteria met')
						this.selectedItems = [];
					}
				}
				this.displayConfig = !this.displayConfig;
			},
			toggleDeviceConfig(item) {
				this.displayDeviceConfig = !this.displayDeviceConfig;
				this.selectedDevice = item;
			},
			async deleteItem() {
				logger.info('deleting vehicle', this.deleteTarget);

				this.deletingItem = true;

				if (this.deleteTarget.devices.length > 0) {
					//unassign devices then delete vehicle
					let deviceObjs = this.deleteTarget.devices;

					// we want to use the org id for the item if it's available, otherwise use the users selected org id
					let org = this.deleteTarget.org_id || this.orgId

					for (const device in deviceObjs) {
						let deviceId = deviceObjs[device].device_id;

						await UnassignDeviceFromVehicle(org, this.deleteTarget["id"], deviceId);
					}
					// There seems to be a delay between unassign a device and being
					// able to delete it. As a work around, we refresh the vehicles 
					// to act as a buffer between operations. 
					const vehicles = await GetOrgVehicles(org, true);

					if (vehicles && vehicles.length > 0) {
						this.vehicles = this.buildTableObj(vehicles);
						
						let res = await this.deleteVehicle(this.deleteTarget);
						logger.info('what is res? ', res);
						if (res) {
							this.deleteLogState = true;
							this.deleteLogRes = {
								msg: `Vehicle deleted`,
								code: 'green'
							};
						}
						else {
							this.deleteLogState = true;
							this.deleteLogRes = {
								msg: `An error occurred`,
								code: 'red'
							};
						}
					}
				} 
				else {
					let res = await this.deleteVehicle(this.deleteTarget);
					logger.info('what is res? ', res);
					if (res) {
						this.deleteLogState = true;
						this.deleteLogRes = {
							msg: `Vehicle deleted`,
							code: 'green'
						};
					}
					else {
						this.deleteLogState = true;
						this.deleteLogRes = {
							msg: `An error occurred`,
							code: 'red'
						};
					}
				}
			},
			async deleteVehicle(vehicle) {
				const index = this.vehicles.indexOf(vehicle);
				const res = await DeleteOrgVehicle(
					vehicle.org_id || this.orgId,
					vehicle["id"]
				);
				if (res) this.vehicles.splice(index, 1);

				return res;
			},
			close() {
				this.dialog = false;
				this.editedItem = Object.assign({}, this.defaultItem);
				this.editedIndex = -1;
			},
			refreshData(data) {
				logger.info('refreshing data ', data)
				// Array of vehicle IDs that are updated
				let vehicleIds = data.IDs;

				// Find the updated vehicles in data table and map new vals to row item directly
				for (var i in this.tableData) {
					for (var j in vehicleIds) {
						if (this.tableData[i].id === vehicleIds[j]) {
							for (const [key, value] of Object.entries(data)) {
								// eslint-disable-next-line no-prototype-builtins
								logger.info('test ', this.tableData[i], key)
								if (this.tableData[i].hasOwnProperty(key)) {
									this.tableData[i][key] = value;
								}
							}
						}
					}
				}
			}
		}
	};
</script>

<style lang="scss">
	.vehicles-search-bar {
    padding-top: 1em;
    padding-left: 1em;
  }
	.reports-action-launch {
    position: relative;
    cursor: pointer;
  }
	.report-action-options {
    display: none;
  }
	.active-options {
    display: block !important;  
  }
	.middle-align {
		margin-bottom: 40px;
	}
	.info-popup-container {
		border: solid #eeeeee 2px !important;
		background-color: white !important;
		-webkit-box-shadow: 1px 2px 6px 3px rgba(0,0,0,0.26); 
		box-shadow: 1px 2px 6px 3px rgba(0,0,0,0.26);
	}
	#vehicle-search-table {
		.v-data-table__wrapper {
			overflow-x: visible !important;
			overflow-y: visible !important;
		}
		.download-container {
			position: absolute;
    	right: 45px;
    	top: 10px;
		}
	}
</style>